import supabase from "@/features/supabase";
import type { Dashboard, DatasetDashboardInfo, GetEmbeddableDashboard } from "@/lib/metabase";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

/**
 * The Hub's Metabase proxy routes. Kept separate from `internalApi` because these
 * routes are admin-gated and need the caller's Supabase bearer token, which we do not
 * want attached to every other internal route.
 */
export const metabaseApi = createApi({
  reducerPath: "metabase",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/metabase/",
    prepareHeaders: async (headers) => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        headers.set("authorization", `Bearer ${data.session.access_token}`);
      }
      return headers;
    },
  }),
  endpoints: (build) => ({
    /** Dashboards with embedding enabled, for the dataset editor's picker. */
    getEmbeddableDashboards: build.query<GetEmbeddableDashboard[], void>({
      query: () => "get-embeddable-dashboards",
    }),
    /** Full detail for one dashboard, including its `parameters`. Admin-gated. */
    getMetabaseDashboard: build.query<Dashboard, number>({
      query: (id) => ({ url: "get-dashboard", params: { id } }),
    }),
    /** Viewer-safe dashboard metadata for a dataset, keyed by embed page id. */
    getDatasetDashboardInfo: build.query<DatasetDashboardInfo, string>({
      query: (embedPageId) => ({ url: "get-dataset-info", params: { embedPageId } }),
    }),
  }),
});

export const { useGetDatasetDashboardInfoQuery, useGetEmbeddableDashboardsQuery, useGetMetabaseDashboardQuery } =
  metabaseApi;

export type { DatasetDashboardInfo };
